import { ReadMoreLink, Text } from "@kvix/ui";
import { Box, CircularProgress, Divider, styled } from "@material-ui/core";
import { subMonths, format, parseISO } from "date-fns";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MfnResponse, useFetchForm, useInvestorData } from "./mfnData";

const today = new Date();
const oneYearAgo = subMonths(new Date(), 12);

const Report: React.FC<{
  data: MfnResponse;
  smallerHeadings?: boolean;
  type?: "Årsredovisning" | "Delårsrapport" | "Bokslutskommunike" | any;
}> = ({ data, smallerHeadings, type }) => (
  <>
    {data &&
      data.items.map((item, itemIndex) => {
        const { attachments, title } = item.content;
        return (
          <div key={itemIndex}>
            {smallerHeadings ? null : <Text variant={"h3"}>{title}</Text>}

            {attachments &&
              attachments.map((attachment, attIndex) => {
                if (type) {
                  if (type.includes(',')) {
                    const types = type.split(',');
                    let included = false;

                    for (let i = 0; i < types.length; i++) {
                      if (attachment.file_title.includes(types[i])) {
                        included = true;
                        break;
                      }
                    }
                    if (!included) {
                      return null;
                    }
                  }
                  
                  if (!type.includes(',') && attachment.file_title.indexOf(type) < 0) {
                    return null;
                  }
                }
                return (
                  <div key={attIndex}>
                    {attIndex == 0 && <Divider component="hr" />}
                    <ReadMoreLink
                      key={attIndex}
                      link={item.content['link'] ? item.content['link'] : attachment.url}
                      linkText={item.content['link'] ? item.content.title : attachment.file_title}
                      date={format(parseISO(item.content.publish_date), 'yyyy.MM.dd')}
                    />
                    {attachments.length > attIndex && (
                      <Divider component="hr" />
                    )}
                  </div>
                );
              })}
          </div>
        );
      })}
  </>
);

const Form: React.FC<{ data: MfnResponse; smallerHeadings?: boolean }> = ({
  data,
  smallerHeadings,
}) => {
  //TODO: Fix this dirty hack and make more general
  const item = data?.items[0];
  const { attachments, title } = item?.content;
  return (
    <>
      {data && (
        <div>
          <Text variant={smallerHeadings ? "h6" : "h3"}>{title}</Text>
          {attachments &&
            attachments.map((attachment, attIndex) => {
              return (
                <div key={attIndex}>
                  {attIndex == 0 && <Divider component="hr" />}
                  <ReadMoreLink
                    key={attIndex}
                    link={attachment.url}
                    linkText={attachment.file_title}
                  />
                  {attachments.length > attIndex && <Divider component="hr" />}
                </div>
              );
            })}
        </div>
      )}
    </>
  );
};

export const MfnFeed: React.FC<{ type: "report" | "ca" }> = ({ type }) => {
  const { t, ready } = useTranslation("mfn");

  const staticSlugs = [
    'kvix-ab-publ-offentliggor-idag-bolagets-delarsrapport-for-perioden-juli-september-2023',
    'kvix-ab-publ-offentliggor-idag-bolagets-delarsrapport-for-perioden-april-juni-2023',
    'kvix-ab-publ-offentliggor-idag-bolagets-delarsrapport-for-perioden-januari-mars-2023',
    'kvix-arsredovisning-for-2022-har-publicerats',
    'kvix-ab-publ-offentliggor-idag-bolagets-bokslutskommunike-for-januari-december-2022',
    'kvix-ab-publ-offentliggor-idag-bolagets-delarsrapport-for-perioden-juli-september-2022',
    'kvix-ab-publ-offentliggor-idag-bolagets-delarsrapport-for-perioden-april-juni-2022',
    'kvix-ab-publ-offentliggor-idag-bolagets-delarsrapport-for-perioden-januari-mars-2022',
    'kvix-arsredovisning-for-2021-har-publicerats',
    'kvix-ab-publ-offentliggor-idag-bolagets-bokslutskommunike-for-januari-december-2021',
    'kvix-ab-publ-offentliggor-idag-bolagets-bokslutskommunike-for-januari-december-2023',
    'kvix-ab-publ-offentliggor-idag-bolagets-delarsrapport-for-perioden-juli-september-2024',
    // 'kallelse-till-arsstamma-i-kvix-ab-publ',
    // 'kvix-ab-publ-s-arsstamma-den-23-maj-2022-stalls-in-av-administrativa-skal-styrelsen-kallar-harmed-till-arsstamma-pa-nytt-att-hallas-den-21-juni-2022-kallelsen-foljer-i-sin-helhet-nedan',
    // 'kommunike-fran-arsstamma-i-kvix-ab-publ-den-21-juni-2022',
    // 'kallelse-till-arsstamma-i-kvix-ab-publ-1',
    // 'kommunike-fran-arsstamma-i-kvix-ab-publ-den-23-maj-2023',
  ];

  const investorReportsAsync = useInvestorData({
    type: "ir",
    tags: "sub:report",
    // from: oneYearAgo,
    // to: today,
  });

  const investorFormAsync = useFetchForm();
  const form = useMemo(() => {
    if (!investorFormAsync?.value) {
      return null;
    }
    return investorFormAsync.value;
  }, [investorFormAsync]);

  const investorReports = useMemo(() => {
    if (!investorReportsAsync?.value) {
      return null;
    }
    return investorReportsAsync.value;
  }, [investorReportsAsync]);

  const caRegulatoryAsync = useInvestorData({
    type: "ir",
    // tags: ["sub:ca", ":regulatory"],
    // filter: `(.properties.tags@>[":regulatory"])`,
    // from: oneYearAgo,
    // to: today,
    lang: "sv",
  });

  const caRegulatory = useMemo(() => {
    if (!caRegulatoryAsync?.value || !investorReports?.items) {
      return null;
    }

    let regulatoryExtended = {
      items: caRegulatoryAsync.value.items.filter(item => (item['properties']['tags'].includes('sub:ci') || item['properties']['tags'].includes('sub:ca')) && item['properties']['tags'].includes(':regulatory'))
    };
    let investorReportsCopy = JSON.parse(JSON.stringify(investorReports));

    for (let i = 0; i < staticSlugs.length; i++) {
      if (!regulatoryExtended.items.find(item => item.content.slug === staticSlugs[i])) {
        let extendedItem = investorReportsCopy.items.find((item: any) => item.content.slug === staticSlugs[i]);

        extendedItem.content['link'] = `https://mfn.se/a/kvix/${staticSlugs[i]}`;
        regulatoryExtended.items.push(extendedItem);
      }
    }

    regulatoryExtended.items.sort((a,b) => {
      return new Date(b.content.publish_date).getTime() - new Date(a.content.publish_date).getTime();
    });

    return regulatoryExtended;
  }, [caRegulatoryAsync, investorReports]);

  const caNonRegulatoryAsync = useInvestorData({
    type: "ir",
    tags: "sub:ca",
    filter: `(not(.properties.tags@>[":regulatory"]))`,
    // from: oneYearAgo,
    // to: today,
    lang: "sv",
  });

  const caNonRegulatory = useMemo(() => {
    if (!caNonRegulatoryAsync?.value) {
      return null;
    }
    return caNonRegulatoryAsync.value;
  }, [caNonRegulatoryAsync]);

  const Reports = styled(Box)({
    "& hr": {
      marginTop: 16,
    },
    "& h2": {
      fontSize: 34,
      letterSpacing: 0.15,
      lineHeight: "37px",
      marginTop: 40,
      marginBottom: 20,
    },
    "& h3": {
      fontSize: 24,
      letterSpacing: 0.18,
      lineHeight: "32px",
      marginTop: 48,
      marginBottom: 24,
    },
    fullWidth: {
      width: "100%",
    },
  });

  const regulatoryLength = caRegulatory?.items.length || 0;
  const nonRegulatoryLength = caNonRegulatory?.items.length || 0;
  // const formLength = form?.items.length || 0;

  if (!ready) {
    return null;
  }

  return (
    <Reports>
      {type === "report" ? (
        <>
          {!investorReportsAsync.loading ? (
            <>
              <Text variant="h3">Finansiella Rapporter</Text>
              <Report
                data={investorReports}
                smallerHeadings={true}
                type="rsredovisning,Delårsrapport,Bokslut"
              />
            </>
          ) : (
            <CircularProgress />
          )}
        </>
      ) : (
        <>
          {!caRegulatoryAsync.loading &&
          !caNonRegulatoryAsync.loading &&
          !investorFormAsync.loading ? (
            <>
              {regulatoryLength + nonRegulatoryLength > 0 && (
                <Text variant="h2">{t("ca.title")}</Text>
              )}
              {regulatoryLength > 0 && (
                <>
                  <Text variant="h3">{t("ca.regulatory")}</Text>
                  <Report data={caRegulatory} smallerHeadings />
                </>
              )}
              {nonRegulatoryLength > 0 && (
                <>
                  <Text variant="h3">{t("ca.nonRegulatory")}</Text>
                  <Report data={caNonRegulatory} smallerHeadings />
                </>
              )}
            </>
          ) : (
            <CircularProgress />
          )}
        </>
      )}
    </Reports>
  );
};

export default MfnFeed;
